import React, { useState, useEffect } from 'react';
import { CalendarIcon } from 'lucide-react';
import dayjs from 'dayjs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { formatMoney } from '@/lib/formatUtils';
import { FormFieldProps } from './types';
import { cn } from '@/lib/utils';

const CellField: React.FC<FormFieldProps> = ({
  value,
  onValueChange,
  onCommit,
  type = 'text',
  disabled = false,
  options,
  error,
  className,
  placeholder,
  inputClassName
}) => {
  const fieldError = error;
  const [displayValue, setDisplayValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);

  // Parse formatted number back to raw number
  const parseNumber = (formattedValue: string): number | null => {
    if (!formattedValue) return null;
    const cleanValue = formattedValue.replace(/[^\d.-]/g, '');
    return parseFloat(cleanValue) || null;
  };

  // Update display value when value prop changes
  useEffect(() => {
    if (type === 'number' && !isFocused) {
      setDisplayValue(formatMoney(value));
    } else if (type !== 'number') {
      setDisplayValue(value !== undefined && value !== null ? value.toString() : '');
    }
  }, [value, type, isFocused]);

  const handleChange = (newValue: any) => {
    onValueChange?.(newValue);
  };

  const resolveOptionValue = (selectedValue: string) => {
    const option = options?.find(item => String(item.value) === selectedValue);
    return option ? option.value : selectedValue;
  };

  const selectedValues = Array.isArray(value) ? value : [];
  const formatDateValue = (nextValue: unknown) => {
    if (!nextValue) return '';

    const parsedDate = dayjs(nextValue as string);
    return parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY') : '';
  };

  return (
    <div className={cn('flex w-full flex-col', className)}>
      {type === 'select' && (
        <select
          value={value === undefined || value === null ? '' : String(value)}
          onChange={e => handleChange(resolveOptionValue(e.target.value))}
          disabled={disabled}
          className={cn(
            'h-8 w-full rounded-none border-0 border-b border-gray-200 bg-transparent px-2 py-1 text-sm outline-none transition-colors hover:border-blue-600 focus:border-blue-600 disabled:cursor-not-allowed disabled:text-black/60',
            inputClassName
          )}
        >
          {options?.map(option => (
            <option key={option.value.toString()} value={String(option.value)}>
              {option.label}
            </option>
          ))}
        </select>
      )}

      {type === 'checkbox' && (
        <Checkbox
          checked={value}
          onCheckedChange={handleChange}
          disabled={disabled}
          className='size-4 rounded-sm border-gray-300'
        />
      )}

      {type === 'date' && (
        <Popover open={!disabled && dateOpen} onOpenChange={setDateOpen}>
          <PopoverTrigger asChild>
            <button
              type='button'
              disabled={disabled}
              className={cn(
                'flex h-8 w-full items-center justify-between rounded-none border-0 border-b border-gray-200 bg-transparent px-2 py-1 text-left text-sm outline-none transition-colors hover:border-blue-600 focus:border-blue-600 disabled:cursor-not-allowed disabled:text-black/60',
                inputClassName
              )}
            >
              <span className='min-w-0 truncate'>{formatDateValue(value) || placeholder || ''}</span>
              <CalendarIcon className='mr-2 size-[18px] shrink-0 p-1 text-[#2563EB]' />
            </button>
          </PopoverTrigger>
          <PopoverContent align='start' className='z-[2000] w-auto rounded-sm p-0'>
            <Calendar
              mode='single'
              selected={value ? dayjs(value as string).toDate() : undefined}
              onSelect={selectedDate => {
                handleChange(selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : null);
                setDateOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      )}

      {type === 'multiselect' && (
        <div className='flex w-full flex-col gap-1'>
          {selectedValues.length > 0 && (
            <div className='flex min-h-6 flex-wrap gap-1 px-1'>
              {selectedValues.map(selectedValue => {
                const label = options?.find(option => option.value === selectedValue)?.label ?? String(selectedValue);
                return (
                  <span
                    key={String(selectedValue)}
                    className='inline-flex h-5 max-w-full items-center truncate rounded bg-gray-100 px-2 text-xs text-gray-700'
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          )}
          <select
            value={selectedValues.map(String)}
            onChange={e => {
              const nextValues = Array.from(e.target.selectedOptions, option => resolveOptionValue(option.value));
              handleChange(nextValues);
            }}
            disabled={disabled}
            multiple
            className={cn(
              'min-h-8 w-full rounded-none border border-gray-200 bg-transparent px-2 py-1 text-sm outline-none transition-colors hover:border-blue-600 focus:border-blue-600 disabled:cursor-not-allowed disabled:text-black/60',
              inputClassName
            )}
          >
            {options?.map(option => (
              <option key={option.value.toString()} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {(type === 'text' || type === 'number') &&
        (disabled ? (
          <div
            className={cn(
              'flex h-[24px] w-full items-center overflow-hidden text-ellipsis whitespace-nowrap px-[8px] py-[4px] text-[13px] text-[#00000099]',
              type === 'number' && 'justify-end'
            )}
          >
            {type === 'number' ? formatMoney(value) : value !== undefined && value !== null ? value : ''}
          </div>
        ) : (
          <input
            type='text'
            autoComplete='off'
            value={
              type === 'number'
                ? isFocused
                  ? displayValue
                  : formatMoney(value)
                : value !== undefined && value !== null
                  ? value
                  : ''
            }
            onChange={e => {
              const inputValue = e.target.value;
              if (type === 'number') {
                let processedValue = inputValue;
                if ((displayValue === '0' || displayValue === '') && inputValue.length > 0) {
                  if (inputValue.match(/^0+[1-9]/)) {
                    processedValue = inputValue.replace(/^0+/, '');
                  } else if (inputValue === '0' && displayValue === '') {
                    processedValue = '0';
                  } else if (displayValue === '0' && inputValue.length === 1 && /[1-9]/.test(inputValue)) {
                    processedValue = inputValue;
                  }
                }
                setDisplayValue(processedValue);
                const numericValue = processedValue.replace(/[^\d.,-]/g, '');
                const parsedValue = parseNumber(numericValue);
                handleChange(parsedValue);
              } else {
                handleChange(inputValue);
              }
            }}
            onFocus={() => {
              if (type === 'number') {
                setIsFocused(true);
                setDisplayValue(value === 0 || value === '0' ? '' : value?.toString() || '');
              }
            }}
            onBlur={e => {
              if (type === 'number') {
                setIsFocused(false);
                const cleanValue = e.target.value.replace(/[^\d.-]/g, '');
                const numericValue = parseFloat(cleanValue) || null;
                handleChange(numericValue);
                if (onCommit) {
                  onCommit(numericValue);
                }
              }
            }}
            onKeyDown={e => {
              const key = e.key.toLowerCase();
              if ((e.ctrlKey || e.metaKey) && key === 'a') {
                e.stopPropagation();
                return;
              }
              if (e.key === ' ') e.stopPropagation();
            }}
            placeholder={placeholder}
            className={cn(
              'h-8 w-full border-0 bg-transparent px-2 py-1 text-[13px] outline-none placeholder:text-black/60 disabled:cursor-not-allowed disabled:text-black/60',
              type === 'number' ? 'text-right' : 'text-left',
              inputClassName
            )}
          />
        ))}

      {fieldError && <span className='mt-1 block text-xs text-red-500'>{fieldError}</span>}
    </div>
  );
};

export default CellField;
